import type ArrayProxy from '@ember/array/proxy';
import Service, { service } from '@ember/service';
import type Store from '@ember-data/store';
import { task, type TaskInstance } from 'ember-concurrency';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type FeatureFlagsService from 'garaje/services/feature-flags';

/**
 * The `visitor-profile` linkage serializes only under an explicit include, and a
 * filter created from a profile carries it in the store but loses it on the next
 * refetch. Every consumer of the link (the linked-profile badge, the read-only
 * matched-field gate) therefore depends on this one include, so it is requested
 * where the list is loaded rather than in any single consumer.
 */
function withVisitorProfileInclude(include: string | undefined, featureFlags: FeatureFlagsService): string | undefined {
  if (!featureFlags.isEnabled('vis-blocklist-linked-profile')) return include;
  return include ? `${include},visitor-profile` : 'visitor-profile';
}

export default class BlocklistFilterService extends Service {
  @service declare store: Store;
  @service declare featureFlags: FeatureFlagsService;

  get locationBlocklistFilters(): ArrayProxy<BlacklistFilter> | BlacklistFilter[] {
    return this.queryByCompanyTask.lastSuccessful?.value || [];
  }

  get propertyBlocklistFilters(): ArrayProxy<BlacklistFilter> | BlacklistFilter[] {
    return this.queryByPropertyTask.lastSuccessful?.value || [];
  }

  // legacy API
  query(companyId: string): TaskInstance<ArrayProxy<BlacklistFilter>> {
    return this.queryByCompanyTask.perform(companyId);
  }

  // "This location" passes filter[applied-location] (the BE self-scopes the company, so it is omitted);
  // "All locations" passes filter[company]. Includes are flag-gated because an include the resource
  // doesn't expose raises InvalidInclude → 400 for every company.
  queryByCompanyTask = task(async (companyId: string, appliedLocationId?: string) => {
    const filter: Record<string, string> = appliedLocationId
      ? { 'applied-location': appliedLocationId }
      : { company: companyId };
    const query: { filter: Record<string, string>; include?: string } = { filter };
    // Assigned only when non-empty: an `include: undefined` own key is not the same as no include.
    const include = withVisitorProfileInclude(
      this.featureFlags.isEnabled('feat-blocklist-location-scoping') ? 'company,locations' : undefined,
      this.featureFlags,
    );
    if (include) query.include = include;
    return await this.store.query('blacklist-filter', query);
  });

  queryByPropertyTask = task(async (propertyId: string) => {
    const query: { filter: Record<string, string>; include?: string } = { filter: { property: propertyId } };
    const include = withVisitorProfileInclude(undefined, this.featureFlags);
    if (include) query.include = include;
    return await this.store.query('blacklist-filter', query);
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    blocklistFilter: BlocklistFilterService;
  }
}
